/**
 * Utility for resolving common dependencies with fallbacks
 * Centralizes dependency injection patterns across diagram modules
 */

/**
 * Resolves common dependencies with fallbacks
 * @param {Object} options - Options containing dependencies
 * @returns {Object} - Object with resolved dependencies
 */
export function resolveDependencies(options = {}) {
  const asyncUtils = options.asyncUtils || 
    (typeof window !== 'undefined' && window.asyncUtils) || 
    {
      sleep: ms => new Promise(resolve => setTimeout(resolve, ms)),
      nextFrame: () => new Promise(resolve => requestAnimationFrame(resolve)),
      transitionPromise: (element, setupFn, timeout) => 
        new Promise(resolve => { setupFn(element); setTimeout(resolve, timeout || 300); }),
      animatePromise: (element, keyframes, options) => 
        new Promise(resolve => {
          try {
            if (element && element.animate) {
              const animation = element.animate(keyframes, options);
              animation.onfinish = () => resolve(animation);
            } else {
              // Fallback for browsers without animation support
              setTimeout(resolve, options.duration || 300);
            }
          } catch (e) {
            console.warn('Animation not supported', e);
            setTimeout(resolve, options.duration || 300);
          }
        })
    };
    
  const eventBus = options.eventBus || 
    (typeof window !== 'undefined' && window.eventBus) || 
    { 
      emit: () => {}, 
      on: () => {}, 
      off: () => {} 
    };
    
  const stateManager = options.stateManager || 
    (typeof window !== 'undefined' && window.stateManager) || 
    { 
      getState: () => ({}), 
      setState: () => {}, 
      subscribe: () => () => {} 
    };
    
  return { 
    asyncUtils, 
    eventBus, 
    stateManager 
  };
}

/**
 * Creates an event manager with registration tracking for cleanup
 * @param {Object} eventBus - The event bus to use
 * @returns {Object} - Event manager with registration and cleanup methods
 */
export function createEventManager(eventBus) {
  const events = [];
  
  /**
   * Register an event listener and track it for cleanup
   * @param {string} eventName - The event name to listen for
   * @param {Function} handler - The event handler
   * @returns {Function} - The handler for removal reference
   */
  function registerEvent(eventName, handler) {
    eventBus.on(eventName, handler);
    events.push({ eventName, handler });
    return handler;
  }
  
  /**
   * Emit an event
   * @param {string} eventName - The event name to emit
   * @param {*} data - The event data
   */
  function emitEvent(eventName, data) {
    eventBus.emit(eventName, data);
  }
  
  /**
   * Clean up all registered events
   */
  function cleanup() {
    events.forEach(({ eventName, handler }) => {
      eventBus.off(eventName, handler);
    });
    events.length = 0;
  }
  
  return {
    on: registerEvent,
    emit: emitEvent,
    cleanup
  };
}